// Shared display formatter for a stored KPI measurement value.
// Boolean KPIs store canonical "met" / "not_met" strings but should
// render as "Met" / "Not met".  Percentage + number KPIs
// fall through to the existing `parseKpiMeasurementValue` path with
// per-type unit selection:
//
//   - percentage: unit comes from the stored value (e.g. "99.5%" → "%").
//   - number: unit comes from `kpi.unit` when set, else the stored
//     value's unit.
//   - boolean: "met" → "Met"; "not_met" → "Not met"; unknown → raw.
//
// Consumers: KpiCard (hero), KpiDetailStats (current + avg), KpiHistory
// row values, and anywhere the "Target = X" display is rendered.

import { type Kpi, type KpiMeasurement } from "../../../../types";
import { classifyBooleanKpiValue } from "../../../../lib/kpi/kpiValueParsers";
import { parseKpiMeasurementValue } from "./parseKpiMeasurementValue";

export interface FormattedKpiValue {
  /** Concatenated display form — what UI should render by default. */
  display: string;
  /** Numeric portion when present (percentage / number); empty on
   *  boolean / unparseable. Split lets callers style hero displays
   *  ("90" large, "%" small). */
  numeric: string;
  /** Unit portion, with a leading space for non-"%" units so it drops
   *  cleanly after the numeric (e.g. " hrs"). Empty on boolean. */
  unit: string;
}

const EM_DASH: FormattedKpiValue = { display: "—", numeric: "—", unit: "" };

function formatUnit(unit: string | null | undefined): string {
  if (!unit) return "";
  return unit === "%" ? unit : ` ${unit}`;
}

/** Format a single KPI value for display. Missing measurement → "—". */
export function formatKpiValue(
  kpi: Kpi,
  measurement?: KpiMeasurement | null,
): FormattedKpiValue {
  if (!measurement) return EM_DASH;

  const valueType = kpi.valueType ?? "percentage";
  const raw = measurement.value ?? "";

  if (valueType === "boolean") {
    // Boolean displays fit the same "numeric" slot that callers use for
    // their hero figure — it's a text token, not a number, but the
    // rendering surface is identical. `unit` stays empty so the hero
    // doesn't emit an extra span.
    const cls = classifyBooleanKpiValue(raw);
    if (cls === "met") return { display: "Met", numeric: "Met", unit: "" };
    if (cls === "not-met") {
      return { display: "Not met", numeric: "Not met", unit: "" };
    }
    // Legacy / malformed boolean values — surface as-is so data gaps
    // aren't silently hidden.
    return raw ? { display: raw, numeric: raw, unit: "" } : EM_DASH;
  }

  const parsed = parseKpiMeasurementValue(raw);
  if (parsed.numeric === null) {
    return raw
      ? { display: raw, numeric: raw, unit: "" }
      : EM_DASH;
  }

  const numericStr = Number.isInteger(parsed.numeric)
    ? String(parsed.numeric)
    : String(Math.round(parsed.numeric * 10) / 10);

  // Number KPIs prefer the KPI's own `kpi.unit`, falling back to the
  // stored value's unit when unset. Percentage KPIs default to "%"
  // when the stored value lacks one (covers "90" vs "90%" drift —
  // legacy writers stored the bare number).
  let unit: string;
  if (valueType === "number") {
    unit = kpi.unit ? formatUnit(kpi.unit) : formatUnit(parsed.unit);
  } else {
    unit = formatUnit(parsed.unit || "%");
  }

  return {
    display: `${numericStr}${unit}`,
    numeric: numericStr,
    unit,
  };
}

/** Format a numeric average (from `computeKpiAverage`) for display.
 *  Returns null when the input is null so callers can suppress the
 *  whole "avg {X}" segment without having to juggle flags. */
export function formatKpiAverage(
  kpi: Kpi,
  value: number | null,
): string | null {
  if (value === null) return null;
  const valueType = kpi.valueType ?? "percentage";
  // Defensive — `computeKpiAverage` should already have returned null
  // for boolean KPIs, but keep the guard so callers can't misuse this.
  if (valueType === "boolean") return null;
  const unit = valueType === "number" && kpi.unit ? formatUnit(kpi.unit) : "%";
  return `${value}${unit}`;
}

/** Format the target string for display. Boolean KPIs render `= Met`;
 *  number KPIs append `kpi.unit` when set (target stores only the
 *  numeric/operator portion for this valueType); percentage KPIs render
 *  the raw target unchanged (the `%` lives inline in the stored string). */
export function formatKpiTarget(kpi: Kpi): string {
  const valueType = kpi.valueType ?? "percentage";
  if (valueType === "boolean") return "= Met";
  if (valueType === "number" && kpi.unit) {
    return `${kpi.target} ${kpi.unit}`;
  }
  return kpi.target;
}
